/**
 * DossierWebCard — le informazioni trovate SUL WEB, in una sezione dichiarata.
 *
 * ⚠️ La separazione è il senso di questo componente. Nella scheda ci sono due
 * tipi di verità, e non devono somigliarsi:
 *  - le NOTE, che sono ciò che l'utente ha raccontato → verità;
 *  - il DOSSIER, che è ciò che ha detto il web → ipotesi con la fonte accanto.
 * Per questo qui ogni affermazione porta il suo link, la data è sempre visibile,
 * e il riquadro ha un trattamento visivo diverso (bordo azzurro «informazione»,
 * non il fondo delle note).
 *
 * Non crea tag: al massimo l'utente li aggiunge a mano dopo aver letto.
 */

import React, { useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, Link, Paper, Stack, Tooltip, Typography,
  alpha,
} from '@mui/material';
import {
  Delete, ExpandLess, ExpandMore, HowToReg, OpenInNew, Public, Refresh, Save,
} from '@mui/icons-material';
import { tokens } from '../../theme';
import {
  cercaDossierWeb, eliminaDossierWeb, salvaDossierWeb, updatePersona,
} from '../../services/reteContattiService';
import type {
  CandidatoWeb, CercaDossierWebResponse, DossierWeb, PersonaRete,
} from '../../types/reteContatti';


/**
 * I profili che POTREBBERO essere la persona. Chi l'ha conosciuta li riconosce in
 * un secondo: il sistema li mostra e non decide al posto suo.
 */
const Candidati: React.FC<{
  candidati: CandidatoWeb[];
  /** Presente solo in revisione: riconoscere la persona risolve l'identità. */
  onRiconosciuto?: (c: CandidatoWeb) => void;
  inCorso?: boolean;
}> = ({ candidati, onRiconosciuto, inCorso }) => {
  if (!candidati.length) return null;
  return (
    <Box>
      <Typography
        sx={{
          fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.1em',
          textTransform: 'uppercase', color: tokens.color.warning, mb: 0.75,
        }}
      >
        Potrebbe essere una di queste persone
      </Typography>
      <Stack spacing={0.75}>
        {candidati.map((c, i) => (
          <Paper
            key={i}
            variant="outlined"
            sx={{ p: 1, borderColor: alpha(tokens.color.warning, 0.3), bgcolor: 'transparent' }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.graphite }}>
              {c.descrizione}
            </Typography>
            {c.perche_potrebbe_essere && (
              <Typography variant="caption" sx={{ color: tokens.color.mist, display: 'block' }}>
                {c.perche_potrebbe_essere}
              </Typography>
            )}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              {c.url && (
                <Link
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontSize: '0.72rem' }}
                >
                  apri il profilo
                  <OpenInNew sx={{ fontSize: 11, ml: 0.25, verticalAlign: 'middle' }} />
                </Link>
              )}
              {onRiconosciuto && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<HowToReg sx={{ fontSize: 16 }} />}
                  disabled={inCorso}
                  onClick={() => onRiconosciuto(c)}
                  // Target confortevole anche col dito su tablet.
                  sx={{ textTransform: 'none', fontSize: '0.72rem', minHeight: 32, py: 0.25 }}
                >
                  è lui
                </Button>
              )}
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

interface Props {
  persona: PersonaRete;
  /** Dossier salvato: il chiamante ricarica la scheda. */
  onSalvato: () => Promise<void>;
  showSnack: (msg: string, severity: 'success' | 'error') => void;
}

export const DossierWebCard: React.FC<Props> = ({ persona, onSalvato, showSnack }) => {
  const [cercando, setCercando] = useState(false);
  const [risultato, setRisultato] = useState<CercaDossierWebResponse | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [aperto, setAperto] = useState(false);

  const dossier = persona.dossier_web ?? null;

  const cerca = async () => {
    setCercando(true);
    try {
      const res = await cercaDossierWeb({
        nomePersona: persona.nome,
        aziendaTesto: persona.azienda_testo || undefined,
        ruoloTesto: persona.ruolo_testo || undefined,
        // Email e LinkedIn sono gli indizi che disambiguano gli omonimi: il
        // dominio dell'email identifica l'azienda meglio del nome scritto a mano.
        email: persona.email || undefined,
        linkedin: persona.linkedin || undefined,
        // Verificatori d'identità: un numero trovato su una pagina la lega alla persona.
        telefoni: (persona.telefoni ?? []).map((t) => t.numero).filter(Boolean),
        // Il contesto aiuta a non confondere gli omonimi.
        contesto: [persona.conosciuto_come, persona.cosa_cerca].filter(Boolean).join(' · ') || undefined,
      });
      setRisultato(res);
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Ricerca non riuscita', 'error');
    } finally {
      setCercando(false);
    }
  };

  /**
   * L'utente riconosce la persona in uno dei candidati.
   *
   * È l'informazione che al motore manca e che l'utente ha già in testa: chi ha
   * conosciuto qualcuno di persona lo riconosce da un profilo in un secondo.
   * Prima i candidati erano un vicolo cieco — il sistema mostrava le opzioni e
   * quella certezza si perdeva.
   *
   * ⚠️ Non è auto-popolamento: scrive SOLO su gesto esplicito, e solo il campo
   * `linkedin` (un dato di reperibilità, non un giudizio). Azienda e ruolo restano
   * di competenza dell'utente: sono il suo piano di verità.
   */
  const riconosci = async (c: CandidatoWeb) => {
    setSalvando(true);
    try {
      const isLinkedin = /linkedin\.com/i.test(c.url);
      if (isLinkedin && c.url) {
        await updatePersona(persona.id, { linkedin: c.url });
        await onSalvato();
      }
      setRisultato(null);
      showSnack(
        isLinkedin
          ? 'Profilo salvato: rifaccio la ricerca con l’identità risolta'
          : 'Rifaccio la ricerca tenendo conto del profilo',
        'success',
      );
      // Ora che l'identità è nota, la ricerca può essere molto più precisa.
      await cerca();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Operazione non riuscita', 'error');
    } finally {
      setSalvando(false);
    }
  };

  const salva = async () => {
    if (!risultato) return;
    setSalvando(true);
    try {
      const daSalvare: DossierWeb = {
        sintesi: risultato.sintesi,
        fatti: risultato.fatti,
        fonti: risultato.fonti,
        ricerche: risultato.ricerche,
        candidati: risultato.candidati,
        trovato: risultato.trovato,
        esito: risultato.esito,
        possibile_omonimia: risultato.possibile_omonimia,
        note_incertezza: risultato.note_incertezza,
        generato_il: new Date().toISOString(),
        // SSOT: il modello lo dichiara il server, non una costante duplicata qui.
        modello: risultato.modello ?? '',
        // 'manuale': l'ha chiesto l'utente → il trigger automatico non lo tocca.
        origine: 'manuale',
      };
      await salvaDossierWeb(persona.id, daSalvare);
      setRisultato(null);
      await onSalvato();
      showSnack('Dossier salvato nella scheda', 'success');
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Salvataggio non riuscito', 'error');
    } finally {
      setSalvando(false);
    }
  };

  const elimina = async () => {
    setSalvando(true);
    try {
      await eliminaDossierWeb(persona.id);
      await onSalvato();
      showSnack('Dossier rimosso', 'success');
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Rimozione non riuscita', 'error');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          // Trattamento visivo diverso dalle note: qui l'informazione viene da fuori.
          borderColor: alpha(tokens.color.info, 0.35),
          bgcolor: alpha(tokens.color.info, 0.04),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: dossier ? 1.5 : 1 }}>
          <Public fontSize="small" sx={{ color: tokens.color.info }} />
          <Typography
            sx={{
              fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: tokens.color.info, flex: 1,
            }}
          >
            Trovato sul web
          </Typography>
          {dossier && (
            <>
              <Typography sx={{ fontFamily: tokens.font.mono, fontSize: '0.7rem', color: tokens.color.mist }}>
                {new Date(dossier.generato_il).toLocaleDateString('it-IT')}
              </Typography>
              <Tooltip title="Cerca di nuovo">
                <IconButton size="small" onClick={cerca} disabled={cercando || salvando}>
                  <Refresh sx={{ fontSize: 16, color: tokens.color.ash }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rimuovi il dossier (le tue note non vengono toccate)">
                <IconButton size="small" onClick={elimina} disabled={cercando || salvando}>
                  <Delete sx={{ fontSize: 16, color: tokens.color.mist }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>

        {!dossier && (
          <Stack spacing={1.25}>
            <Typography variant="body2" sx={{ color: tokens.color.ash }}>
              Cerca informazioni professionali pubbliche su {persona.nome.split(' ')[0]}: azienda,
              ruolo, settore, presenza online. Ogni affermazione arriva con la sua fonte.
            </Typography>
            <Box>
              <Button
                variant="outlined"
                startIcon={cercando ? <CircularProgress size={16} /> : <Public />}
                onClick={cerca}
                disabled={cercando}
                sx={{ textTransform: 'none', minHeight: 40 }}
              >
                {cercando ? 'Sto cercando…' : 'Cerca sul web'}
              </Button>
            </Box>
            <Typography variant="caption" sx={{ color: tokens.color.mist }}>
              Resta separato dalle tue note: quello che racconti tu fa testo, questo è
              un'informazione di terzi da verificare.
            </Typography>
          </Stack>
        )}

        {dossier && (
          <Stack spacing={1.25}>
            {dossier.possibile_omonimia && (
              <Alert severity="warning" sx={{ py: 0 }}>
                Potrebbe trattarsi di un'altra persona con lo stesso nome.
                {dossier.note_incertezza ? ` ${dossier.note_incertezza}` : ''}
              </Alert>
            )}

            {dossier.sintesi ? (
              <Typography variant="body2" sx={{ color: tokens.color.graphite, whiteSpace: 'pre-wrap' }}>
                {dossier.sintesi}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color: tokens.color.mist, fontStyle: 'italic' }}>
                {dossier.note_incertezza || 'Nessuna informazione trovata.'}
              </Typography>
            )}

            <Candidati candidati={dossier.candidati ?? []} />

            {dossier.fatti.length > 0 && (
              <Stack spacing={0.75}>
                {dossier.fatti.map((f, i) => (
                  <Stack key={i} direction="row" spacing={0.75} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 5, height: 5, borderRadius: '50%', mt: '7px', flexShrink: 0,
                        bgcolor: f.confidenza === 'alta'
                          ? tokens.color.success
                          : f.confidenza === 'bassa' ? tokens.color.mist : tokens.color.info,
                      }}
                    />
                    <Typography variant="body2" sx={{ color: tokens.color.ash, flex: 1 }}>
                      {f.affermazione}
                      {f.fonte_url && f.fonte_verificata !== false && (
                        <Link
                          href={f.fonte_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ ml: 0.5, fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                        >
                          fonte
                          <OpenInNew sx={{ fontSize: 11, ml: 0.25, verticalAlign: 'middle' }} />
                        </Link>
                      )}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}

            {(dossier.fonti.length > 0 || dossier.ricerche.length > 0) && (
              <Box>
                <Button
                  size="small"
                  onClick={() => setAperto((v) => !v)}
                  endIcon={aperto ? <ExpandLess /> : <ExpandMore />}
                  sx={{ textTransform: 'none', color: tokens.color.mist, fontSize: '0.75rem' }}
                >
                  {dossier.fonti.length} fonti consultate
                </Button>
                <Collapse in={aperto}>
                  <Stack spacing={0.5} sx={{ pl: 1, pt: 0.5 }}>
                    {dossier.fonti.map((f, i) => (
                      <Link
                        key={i}
                        href={f.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ fontSize: '0.75rem', color: tokens.color.ash }}
                      >
                        {f.titolo || f.url}
                      </Link>
                    ))}
                    {dossier.ricerche.length > 0 && (
                      <Typography variant="caption" sx={{ color: tokens.color.mist, mt: 0.5 }}>
                        Ricerche eseguite: {dossier.ricerche.join(' · ')}
                      </Typography>
                    )}
                  </Stack>
                </Collapse>
              </Box>
            )}
          </Stack>
        )}
      </Paper>

      {/* Revisione del risultato: come per la nota vocale, niente si salva da solo */}
      <Dialog
        open={!!risultato}
        onClose={salvando ? undefined : () => setRisultato(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: tokens.font.display, fontWeight: 600, pb: 0.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Public fontSize="small" sx={{ color: tokens.color.info }} />
            <span>Cosa ho trovato sul web</span>
          </Stack>
          <Typography variant="body2" sx={{ color: tokens.color.ash, mt: 0.5, fontFamily: tokens.font.body }}>
            Informazioni di terzi, non tue: controlla le fonti prima di salvarle nella scheda.
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {risultato && !risultato.trovato && (
            <Stack spacing={1}>
              {/* Tre esiti diversi, tre messaggi: dire «non c'è nulla» quando la
                  ricerca è invece FALLITA è ciò che ha fatto credere che sul web
                  non ci fosse nessun Dario Liguori. */}
              {risultato.esito === 'errore_tecnico' ? (
                <Alert severity="warning">
                  La ricerca non è andata a buon fine — non vuol dire che non ci sia
                  nulla. Riprova tra poco.
                </Alert>
              ) : (
                <Alert severity="info">
                  Nessun risultato che contenga questo nome. Se hai il LinkedIn o
                  l'email aziendale, aggiungili alla scheda: la ricerca riparte da sola.
                </Alert>
              )}
              {risultato.note_incertezza && (
                <Typography variant="body2" sx={{ color: tokens.color.ash }}>
                  {risultato.note_incertezza}
                </Typography>
              )}
              {risultato.ricerche.length > 0 && (
                <Typography variant="caption" sx={{ color: tokens.color.mist }}>
                  Ricerche provate: {risultato.ricerche.join(' · ')}
                </Typography>
              )}
            </Stack>
          )}

          {risultato?.trovato && (
            <Stack spacing={1.5}>
              {risultato.possibile_omonimia && (
                <Alert severity="warning">
                  <strong>Attenzione all'omonimia.</strong>{' '}
                  {risultato.note_incertezza || 'Il nome è comune e il collegamento non è certo.'}
                </Alert>
              )}

              {risultato.sintesi && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {risultato.sintesi}
                </Typography>
              )}

              <Candidati
                candidati={risultato.candidati ?? []}
                onRiconosciuto={riconosci}
                inCorso={salvando || cercando}
              />

              {risultato.fatti.map((f, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                  <Chip
                    label={f.confidenza}
                    size="small"
                    sx={{
                      height: 19, fontSize: '0.62rem', flexShrink: 0,
                      bgcolor: alpha(
                        f.confidenza === 'alta' ? tokens.color.success
                          : f.confidenza === 'bassa' ? tokens.color.mist : tokens.color.info,
                        0.14,
                      ),
                    }}
                  />
                  <Typography variant="body2" sx={{ color: tokens.color.ash, flex: 1 }}>
                    {f.affermazione}
                    {f.fonte_url && f.fonte_verificata !== false && (
                      <Link
                        href={f.fonte_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ ml: 0.5, fontSize: '0.72rem' }}
                      >
                        fonte
                      </Link>
                    )}
                  </Typography>
                </Stack>
              ))}

              {risultato.fonti.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: tokens.color.mist }}>
                    Fonti consultate:
                  </Typography>
                  <Stack spacing={0.25} sx={{ pl: 1 }}>
                    {risultato.fonti.map((f, i) => (
                      <Link
                        key={i}
                        href={f.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ fontSize: '0.75rem' }}
                      >
                        {f.titolo || f.url}
                      </Link>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setRisultato(null)} disabled={salvando} color="inherit">
            Scarta
          </Button>
          <Button
            variant="contained"
            onClick={salva}
            disabled={salvando || !risultato?.trovato}
            startIcon={salvando ? <CircularProgress size={16} color="inherit" /> : <Save />}
          >
            Salva nella scheda
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
